/* eslint-disable react/prop-types */
import { useTranslation } from "react-i18next";
import {
  getProductBadgeOption,
  normalizeProductBadgesForSave,
  cleanBadgeIconName,
} from "../../../constants/productBadgeOptions";
import ProductBadgeIcon from "./ProductBadgeIcon";
import {
  getNutritionOption,
  normalizeNutritionValuesForSave,
} from "../../../constants/nutritionOptions";
import {
  getZoneCountryFlagUrl,
  getZoneCountryLabel,
} from "../../../constants/zoneCountryOptions";
import "./NutritionValuesEditor.css";
import "./ProductMetaDisplay.css";
import config from "../../../../config.json";

const COUNTRY_BADGE_TYPE = "zone";

function getBadgeDisplayLabel(type, value, lang) {
  const option = getProductBadgeOption(type);
  if (type === "customized" && value) return value;
  if (type === COUNTRY_BADGE_TYPE && value) {
    return getZoneCountryLabel(value, lang);
  }
  return option.label;
}

function badgeIconUrl(filename) {
  const clean = cleanBadgeIconName(filename);
  if (!clean) return "";
  const base = String(config.UPLOADS_SERVER || "").replace(/\/+$/, "");
  return base ? `${base}/product/${encodeURIComponent(clean)}` : "";
}

function renderBadgeIcon(type, value, icon) {
  const customIcon = type === "customized" ? badgeIconUrl(icon) : "";
  if (customIcon) {
    return (
      <img src={customIcon} alt="" className="product-meta-chip__flag" />
    );
  }
  if (type === COUNTRY_BADGE_TYPE && value) {
    return (
      <img
        src={getZoneCountryFlagUrl(value)}
        alt=""
        className="product-meta-chip__flag"
      />
    );
  }
  return (
    <ProductBadgeIcon
      item={{ type, value, icon }}
      className="product-meta-chip__icon"
    />
  );
}

function BadgeOverlayChip({ type, value, icon, lang }) {
  if (type === COUNTRY_BADGE_TYPE && value) {
    return (
      <span
        className="product-meta-chip product-meta-chip--flag-only"
        title={getBadgeDisplayLabel(type, value, lang)}
      >
        <img
          src={getZoneCountryFlagUrl(value)}
          alt={getBadgeDisplayLabel(type, value, lang)}
          className="product-meta-chip__flag product-meta-chip__flag--corner"
        />
      </span>
    );
  }

  return (
    <span className="product-meta-chip">
      {renderBadgeIcon(type, value, icon)}
      <span className="product-meta-chip__text">
        {getBadgeDisplayLabel(type, value, lang)}
      </span>
    </span>
  );
}

export default function ProductMetaDisplay({
  productBadges = [],
  nutritionValues = [],
  legacyProductBadge = "",
  variant = "inline",
}) {
  const { i18n, t } = useTranslation();
  const lang = String(i18n.language || "en").split("-")[0].toLowerCase();
  const badges = normalizeProductBadgesForSave(productBadges, legacyProductBadge);
  const nutrition = normalizeNutritionValuesForSave(nutritionValues);

  if (variant === "overlay") {
    if (!badges.length) return null;

    const countryBadges = badges.filter(
      (item) => item.type === COUNTRY_BADGE_TYPE && item.value
    );
    const otherBadges = badges.filter(
      (item) => !(item.type === COUNTRY_BADGE_TYPE && item.value)
    );

    return (
      <>
        {countryBadges.map((item) => (
          <div
            key={`flag-${item.type}`}
            className="product-meta-flag-corner"
            title={getBadgeDisplayLabel(item.type, item.value, lang)}
            aria-label={getBadgeDisplayLabel(item.type, item.value, lang)}
          >
            <img
              src={getZoneCountryFlagUrl(item.value)}
              alt=""
              className="product-meta-flag-corner__img"
            />
          </div>
        ))}
        {otherBadges.length > 0 ? (
          <div className="product-meta-overlay" aria-label={t("productBadgesLabel")}>
            {otherBadges.map((item) => (
              <BadgeOverlayChip
                key={`${item.type}-${item.value || ""}`}
                type={item.type}
                value={item.value}
                icon={item.icon}
                lang={lang}
              />
            ))}
          </div>
        ) : null}
      </>
    );
  }

  if (!nutrition.length) return null;

  return (
    <div className="product-meta-display product-meta-display--nutrition">
      <span className="product-meta-display__label">{t("productNutritionLabel")}</span>
      <div className="nutrition-values-summary product-meta-display__chips">
        {nutrition.map((item) => {
          const option = getNutritionOption(item.type);
          return (
            <span key={item.type} className="nutrition-values-summary-chip">
              <span className="nutrition-values-summary-chip__icon" aria-hidden="true">
                {option.icon}
              </span>
              <span className="nutrition-values-summary-chip__text">
                {option.label}
                {item.value ? ` · ${item.value}` : ""}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
